import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MSFLogo from '../components/MSFLogo';
import PageBackground from '../components/PageBackground';
import { Edit, TrendingUp, Car, Bike, BookOpen, Calendar, Pencil, Check, X } from 'lucide-react';
import WelcomeTutorial from '../components/WelcomeTutorial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('racing');
  const [settings, setSettings] = useState({});
  
  // Racing data
  const [racingSelection, setRacingSelection] = useState(null);
  const [racingLeaderboardPosition, setRacingLeaderboardPosition] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [cars, setCars] = useState([]);
  const [racingTuners, setRacingTuners] = useState([]);
  
  // Moto data
  const [motoSelection, setMotoSelection] = useState(null);
  const [motoLeaderboardPosition, setMotoLeaderboardPosition] = useState(null);
  const [riders, setRiders] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [motoTuners, setMotoTuners] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  
  // Team name editing
  const [editingTeamName, setEditingTeamName] = useState(null); // 'racing' | 'moto' | null
  const [tempTeamName, setTempTeamName] = useState('');
  const [nextRound, setNextRound] = useState(null);
  const [countdownText, setCountdownText] = useState('');

  const saveTeamName = async (league) => {
    try {
      const name = tempTeamName.trim();
      if (!name) { toast.error('Team name cannot be empty'); return; }
      await axios.put(
        `${BACKEND_URL}/api/team-name?league=${league}&team_name=${encodeURIComponent(name)}`,
        {}, { withCredentials: true }
      );
      if (league === 'racing') {
        setRacingSelection(prev => ({ ...prev, team_name: name }));
      } else {
        setMotoSelection(prev => ({ ...prev, team_name: name }));
      }
      setEditingTeamName(null);
      toast.success('Team name updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update team name');
    }
  };

  useEffect(() => {
    fetchData();
    fetchNextRound();
    // Show rules prompt on every fresh login unless permanently dismissed
    const rulesDismissed = localStorage.getItem('msf_rules_dismissed');
    if (!rulesDismissed) {
      // Fresh login detected (both form login and Google OAuth pass user in state)
      const isFreshLogin = location.state?.user;
      const rulesShownThisSession = sessionStorage.getItem('msf_rules_shown');
      if (isFreshLogin || !rulesShownThisSession) {
        setShowTutorial(true);
        sessionStorage.setItem('msf_rules_shown', 'true');
      }
    }
    // fetchData/fetchNextRound are stable function expressions — run once on mount.
  }, []);

  const fetchData = async () => {
    try {
      // Fetch user, settings, and racing data
      const [userRes, settingsRes, racingSelectionRes, racingLeaderboardRes, driversRes, carsRes, racingTunersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/system/settings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/my-selection`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/leaderboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/drivers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/cars`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tuners`, { withCredentials: true }),
      ]);

      setUser(userRes.data);
      setSettings(settingsRes.data);
      setRacingSelection(racingSelectionRes.data);
      setDrivers(driversRes.data);
      setCars(carsRes.data);
      setRacingTuners(racingTunersRes.data);

      const racingPosition = racingLeaderboardRes.data.findIndex(
        (entry) => entry.user.user_id === userRes.data.user_id
      );
      if (racingPosition !== -1) {
        setRacingLeaderboardPosition(racingLeaderboardRes.data[racingPosition]);
      }

      // Fetch Moto data
      const [motoSelectionRes, motoLeaderboardRes, ridersRes, bikesRes, motoTunersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/moto/my-selection`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/leaderboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/riders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/bikes`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/tuners`, { withCredentials: true }),
      ]);

      setMotoSelection(motoSelectionRes.data);
      setRiders(ridersRes.data);
      setBikes(bikesRes.data);
      setMotoTuners(motoTunersRes.data);

      const motoPosition = motoLeaderboardRes.data.findIndex(
        (entry) => entry.user.user_id === userRes.data.user_id
      );
      if (motoPosition !== -1) {
        setMotoLeaderboardPosition(motoLeaderboardRes.data[motoPosition]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const fetchNextRound = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/round-schedule/public`);
      if (res.data.next_round) {
        setNextRound(res.data.next_round);
      }
    } catch (error) {
      console.error('Failed to fetch round schedule:', error);
    }
  };

  useEffect(() => {
    if (!nextRound?.scheduled_datetime) return;
    const updateCountdown = () => {
      const target = new Date(nextRound.scheduled_datetime).getTime();
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) { setCountdownText('Starting soon...'); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      parts.push(`${mins}m`);
      parts.push(`${secs}s`);
      setCountdownText(parts.join(' '));
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextRound]);

  const getDriverById = (id) => drivers.find((d) => d.driver_id === id);
  const getCarById = (id) => cars.find((c) => c.car_id === id);
  const getRacingTunerById = (id) => racingTuners.find((t) => t.team_id === id || t.tuner_id === id);
  
  const getRiderById = (id) => riders.find((r) => r.rider_id === id);
  const getBikeById = (id) => bikes.find((b) => b.bike_id === id);
  const getMotoTunerById = (id) => motoTuners.find((t) => t.tuner_id === id);

  const hasRacingSelection = racingSelection?.drivers?.length > 0;
  const hasMotoSelection = motoSelection?.riders?.length > 0;

  // Dynamic race calendar from settings — sorted once, memoized so JSX doesn't re-sort per render.
  const raceCalendar = useMemo(() => [
    { round: 1, dates: settings.calendar_round1 || '16-17 May', month: (settings.calendar_round1 || '16-17 May').split(' ').pop() },
    { round: 2, dates: settings.calendar_round2 || '4-5 July', month: (settings.calendar_round2 || '4-5 July').split(' ').pop() },
    { round: 3, dates: settings.calendar_round3 || '29-30 August', month: (settings.calendar_round3 || '29-30 August').split(' ').pop() },
    { round: 4, dates: settings.calendar_round4 || '17-18 October', month: (settings.calendar_round4 || '17-18 October').split(' ').pop() },
    { round: 5, dates: settings.calendar_round5 || '5-6 December', month: (settings.calendar_round5 || '5-6 December').split(' ').pop() },
  ].sort((a, b) => a.round - b.round), [settings.calendar_round1, settings.calendar_round2, settings.calendar_round3, settings.calendar_round4, settings.calendar_round5]);

  return (
    <PageBackground pageKey="dashboard">
    <div className="min-h-screen">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-heading font-black text-5xl mb-2" data-testid="dashboard-title">
            {settings.dashboard_welcome_prefix || 'WELCOME BACK,'} <span className="text-primary">{user?.name?.toUpperCase()}</span>
          </h1>
          <p className="text-zinc-400">{settings.dashboard_subtitle || 'Your fantasy racing command center'}</p>
        </div>

        {/* Series Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveTab('racing')}
            data-testid="racing-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeTab === 'racing'
                ? 'bg-primary text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Car size={20} />
            <span>{settings.dashboard_racing_tab_label || <MSFLogo suffix="Racing" size="sm" />}</span>
          </button>
          <button
            onClick={() => setActiveTab('moto')}
            data-testid="moto-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeTab === 'moto'
                ? 'bg-secondary text-black'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Bike size={20} />
            <span>{settings.dashboard_moto_tab_label || <MSFLogo suffix="" size="sm" />}</span>
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            data-testid="rules-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeTab === 'rules'
                ? 'bg-yellow-500 text-black'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <BookOpen size={20} />
            <span>Scoring</span>
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            data-testid="calendar-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeTab === 'calendar'
                ? 'bg-blue-500 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Calendar size={20} />
            <span>Calendar</span>
          </button>
        </div>

        {/* Rules Tab Content */}
        {activeTab === 'rules' && (
          <div className="bg-card border border-zinc-800 p-8" data-testid="scoring-content">
            <h2 className="font-heading font-bold text-3xl mb-6">
              <span className="text-yellow-500">SCORING</span> SYSTEM
            </h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="font-heading font-bold text-xl text-primary mb-3">{settings.rules_points_title || 'Points System'}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { pos: '1st', pts: 100 },
                    { pos: '2nd', pts: 85 },
                    { pos: '3rd', pts: 75 },
                    { pos: '4th', pts: 65 },
                    { pos: '5th', pts: 60 },
                    { pos: '6th', pts: 55 },
                    { pos: '7th', pts: 50 },
                    { pos: '8th', pts: 45 },
                    { pos: '9th', pts: 40 },
                    { pos: '10th', pts: 35 },
                    { pos: '11th', pts: 30 },
                    { pos: '12th', pts: 25 },
                    { pos: '13th', pts: 20 },
                    { pos: '14th', pts: 10 },
                    { pos: '15th', pts: 5 },
                  ].map((item) => (
                    <div key={item.pos} className="bg-zinc-900 border border-zinc-800 p-3 text-center">
                      <p className="text-zinc-400 text-sm">{item.pos}</p>
                      <p className="font-mono font-bold text-xl text-secondary">{item.pts}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Tab Content */}
        {activeTab === 'calendar' && (
          <div className="bg-card border border-zinc-800 p-8" data-testid="calendar-content">
            <h2 className="font-heading font-bold text-3xl mb-6">
              <span className="text-blue-500">{settings.calendar_title?.split(' ')[0] || 'RACE'}</span> {settings.calendar_title?.split(' ').slice(1).join(' ') || 'CALENDAR 2026'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {raceCalendar.map((race) => (
                <div 
                  key={race.round} 
                  className="bg-zinc-900 border-2 border-zinc-800 hover:border-blue-500 transition-colors p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-blue-500 text-white px-3 py-1 font-heading font-bold text-sm">
                      ROUND {race.round}
                    </span>
                    <span className="text-zinc-500 text-sm">{race.month}</span>
                  </div>
                  <p className="font-heading font-bold text-2xl text-white">{race.dates}</p>
                  <p className="text-zinc-400 mt-2">{settings.calendar_venue || 'Sepang International Circuit'}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-zinc-900 border border-zinc-700 p-4 text-center">
              {nextRound ? (
                <div data-testid="next-round-countdown">
                  <p className="text-zinc-400 text-sm mb-1">NEXT ROUND</p>
                  <p className="text-white font-bold text-lg">{nextRound.round_name || `Round ${nextRound.round_number}`}</p>
                  <p className="text-blue-400 font-mono text-2xl mt-1">{countdownText}</p>
                </div>
              ) : (
                <p className="text-zinc-400">
                  {settings.calendar_footer_text || 'Make sure to lock in your teams before playing!'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Racing Tab Content */}
        {activeTab === 'racing' && (
          <>
            {racingLeaderboardPosition && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-card border border-zinc-800 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-400 mb-1">Racing Rank</p>
                      <p className="font-mono text-4xl font-bold text-primary" data-testid="racing-rank">
                        #{racingLeaderboardPosition.rank}
                      </p>
                    </div>
                    <TrendingUp className="text-secondary" size={40} />
                  </div>
                </div>
                <div className="bg-card border border-zinc-800 p-6">
                  <div>
                    <p className="text-sm text-zinc-400 mb-1">Total Points</p>
                    <p className="font-mono text-4xl font-bold" data-testid="racing-points">
                      {racingLeaderboardPosition.total_points}
                    </p>
                  </div>
                </div>
                <div className="bg-card border border-zinc-800 p-6">
                  <div>
                    <p className="text-sm text-zinc-400 mb-1">Penalty Points</p>
                    <p className="font-mono text-4xl font-bold text-red-500" data-testid="racing-penalties">
                      -{racingLeaderboardPosition.penalty_points}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-card border border-zinc-800 p-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading font-bold text-3xl" data-testid="racing-garage-section">
                  {settings.dashboard_garage_title?.split(' ')[0] || 'MY'} <span className="text-primary">{settings.dashboard_garage_title?.split(' ').slice(1).join(' ') || 'GARAGE'}</span>
                </h2>
                <button
                  onClick={() => navigate('/team-selection')}
                  data-testid="edit-racing-team-button"
                  className="btn-skew bg-primary text-white px-6 py-2 font-bold uppercase tracking-wide flex items-center space-x-2"
                >
                  <Edit size={18} />
                  <span>{hasRacingSelection ? (settings.dashboard_edit_button_text || 'Edit Team') : 'Select Team'}</span>
                </button>
              </div>

              {/* Team Name */}
              {hasRacingSelection && (
                <div className="mb-8 flex items-center gap-2" data-testid="racing-team-name-display">
                  {editingTeamName === 'racing' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={tempTeamName}
                        onChange={(e) => setTempTeamName(e.target.value.slice(0, 30))}
                        className="bg-zinc-900 border border-primary px-3 py-1.5 text-white font-heading font-bold focus:outline-none"
                        data-testid="racing-team-name-edit-input"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && saveTeamName('racing')}
                      />
                      <button onClick={() => saveTeamName('racing')} className="p-1.5 bg-green-600 hover:bg-green-500 text-white transition-colors" data-testid="racing-team-name-save"><Check size={16} /></button>
                      <button onClick={() => setEditingTeamName(null)} className="p-1.5 bg-zinc-700 hover:bg-zinc-600 text-white transition-colors"><X size={16} /></button>
                    </div>
                  ) : (
                    <>
                      <span className="text-zinc-400 text-sm uppercase tracking-wider">Team:</span>
                      <span className="font-heading font-bold text-lg text-white">{racingSelection.team_name || 'Unnamed Team'}</span>
                      <button
                        onClick={() => { setEditingTeamName('racing'); setTempTeamName(racingSelection.team_name || ''); }}
                        className="p-1 text-zinc-500 hover:text-primary transition-colors"
                        data-testid="racing-team-name-edit-btn"
                      >
                        <Pencil size={14} />
                      </button>
                    </>
                  )}
                </div>
              )}

              {!hasRacingSelection ? (
                <div className="text-center py-16" data-testid="no-racing-selection">
                  <p className="text-zinc-400 text-xl mb-6">
                    {settings.dashboard_no_selection_text || "You haven't selected your racing team yet!"}
                  </p>
                  <button
                    onClick={() => navigate('/team-selection')}
                    className="btn-skew bg-primary text-white px-8 py-3 font-heading font-bold uppercase tracking-wider"
                  >
                    <span>Build Your Team</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  <div>
                    <h3 className="font-heading font-bold text-xl mb-4 text-secondary">DRIVERS</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {racingSelection.drivers.map((driverId) => {
                        const driver = getDriverById(driverId);
                        return driver ? (
                          <div
                            key={driver.driver_id}
                            data-testid={`selected-driver-${driver.driver_id}`}
                            className="bg-zinc-900 border border-zinc-800 p-4 flex items-center space-x-4"
                          >
                            {driver.image_url ? (
                              <img src={driver.image_url} alt={driver.name} className="w-16 h-16 object-cover" />
                            ) : (
                              <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center font-mono font-bold text-2xl">
                                #{driver.number}
                              </div>
                            )}
                            <div>
                              <p className="font-bold">{driver.name}</p>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-heading font-bold text-xl mb-4 text-secondary">CARS</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {racingSelection.cars.map((carId) => {
                        const car = getCarById(carId);
                        return car ? (
                          <div
                            key={car.car_id}
                            data-testid={`selected-car-${car.car_id}`}
                            className="bg-zinc-900 border border-zinc-800 p-4 flex items-center space-x-4"
                          >
                            {car.image_url ? (
                              <img src={car.image_url} alt={car.name} className="w-16 h-16 object-cover" />
                            ) : (
                              <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center font-mono font-bold text-2xl">
                                #{car.number}
                              </div>
                            )}
                            <div>
                              <p className="font-bold">{car.name}</p>
                              <p className="text-sm text-zinc-400">{car.tuner}</p>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-heading font-bold text-xl mb-4 text-secondary">TUNER</h3>
                    <div className="bg-zinc-900 border border-zinc-800 p-4 flex items-center space-x-4 max-w-md">
                      {(() => {
                        const tuner = getRacingTunerById(racingSelection.team || racingSelection.tuner);
                        return tuner ? (
                          <>
                            {tuner.image_url ? (
                              <img src={tuner.image_url} alt={tuner.name} className="w-16 h-16 object-cover" />
                            ) : (
                              <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center font-bold text-lg">
                                {tuner.name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <p className="font-bold text-lg">{tuner.name}</p>
                            </div>
                          </>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* MotoMSF Tab Content */}
        {activeTab === 'moto' && (
          <>
            {motoLeaderboardPosition && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-card border border-zinc-800 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-400 mb-1">MotoMSF Rank</p>
                      <p className="font-mono text-4xl font-bold text-secondary" data-testid="moto-rank">
                        #{motoLeaderboardPosition.rank}
                      </p>
                    </div>
                    <TrendingUp className="text-primary" size={40} />
                  </div>
                </div>
                <div className="bg-card border border-zinc-800 p-6">
                  <div>
                    <p className="text-sm text-zinc-400 mb-1">Total Points</p>
                    <p className="font-mono text-4xl font-bold" data-testid="moto-points">
                      {motoLeaderboardPosition.total_points}
                    </p>
                  </div>
                </div>
                <div className="bg-card border border-zinc-800 p-6">
                  <div>
                    <p className="text-sm text-zinc-400 mb-1">Penalty Points</p>
                    <p className="font-mono text-4xl font-bold text-red-500" data-testid="moto-penalties">
                      -{motoLeaderboardPosition.penalty_points}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-card border border-zinc-800 p-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading font-bold text-3xl" data-testid="moto-garage-section">
                  MY <span className="text-secondary">GARAGE</span>
                </h2>
                <button
                  onClick={() => navigate('/moto-team-selection')}
                  data-testid="edit-moto-team-button"
                  className="btn-skew bg-secondary text-black px-6 py-2 font-bold uppercase tracking-wide flex items-center space-x-2"
                >
                  <Edit size={18} />
                  <span>{hasMotoSelection ? 'Edit Team' : 'Select Team'}</span>
                </button>
              </div>

              {/* Team Name */}
              {hasMotoSelection && (
                <div className="mb-8 flex items-center gap-2" data-testid="moto-team-name-display">
                  {editingTeamName === 'moto' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={tempTeamName}
                        onChange={(e) => setTempTeamName(e.target.value.slice(0, 30))}
                        className="bg-zinc-900 border border-secondary px-3 py-1.5 text-white font-heading font-bold focus:outline-none"
                        data-testid="moto-team-name-edit-input"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && saveTeamName('moto')}
                      />
                      <button onClick={() => saveTeamName('moto')} className="p-1.5 bg-green-600 hover:bg-green-500 text-white transition-colors" data-testid="moto-team-name-save"><Check size={16} /></button>
                      <button onClick={() => setEditingTeamName(null)} className="p-1.5 bg-zinc-700 hover:bg-zinc-600 text-white transition-colors"><X size={16} /></button>
                    </div>
                  ) : (
                    <>
                      <span className="text-zinc-400 text-sm uppercase tracking-wider">Team:</span>
                      <span className="font-heading font-bold text-lg text-white">{motoSelection.team_name || 'Unnamed Team'}</span>
                      <button
                        onClick={() => { setEditingTeamName('moto'); setTempTeamName(motoSelection.team_name || ''); }}
                        className="p-1 text-zinc-500 hover:text-secondary transition-colors"
                        data-testid="moto-team-name-edit-btn"
                      >
                        <Pencil size={14} />
                      </button>
                    </>
                  )}
                </div>
              )}

              {!hasMotoSelection ? (
                <div className="text-center py-16" data-testid="no-moto-selection">
                  <p className="text-zinc-400 text-xl mb-6">
                    You haven&apos;t selected your MotoMSF team yet!
                  </p>
                  <button
                    onClick={() => navigate('/moto-team-selection')}
                    className="btn-skew bg-secondary text-black px-8 py-3 font-heading font-bold uppercase tracking-wider"
                  >
                    <span>Build Your Team</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  <div>
                    <h3 className="font-heading font-bold text-xl mb-4 text-primary">RIDERS</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {motoSelection.riders.map((riderId) => {
                        const rider = getRiderById(riderId);
                        return rider ? (
                          <div
                            key={rider.rider_id}
                            data-testid={`selected-rider-${rider.rider_id}`}
                            className="bg-zinc-900 border border-zinc-800 p-4 flex items-center space-x-4"
                          >
                            {rider.image_url ? (
                              <img src={rider.image_url} alt={rider.name} className="w-16 h-16 object-cover" />
                            ) : (
                              <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center font-mono font-bold text-2xl">
                                #{rider.number}
                              </div>
                            )}
                            <div>
                              <p className="font-bold">{rider.name}</p>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-heading font-bold text-xl mb-4 text-primary">BIKES</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {motoSelection.bikes.map((bikeId) => {
                        const bike = getBikeById(bikeId);
                        return bike ? (
                          <div
                            key={bike.bike_id}
                            data-testid={`selected-bike-${bike.bike_id}`}
                            className="bg-zinc-900 border border-zinc-800 p-4 flex items-center space-x-4"
                          >
                            {bike.image_url ? (
                              <img src={bike.image_url} alt={bike.name} className="w-16 h-16 object-cover" />
                            ) : (
                              <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center font-mono font-bold text-2xl">
                                #{bike.number}
                              </div>
                            )}
                            <div>
                              <p className="font-bold">{bike.name}</p>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-heading font-bold text-xl mb-4 text-primary">TUNER</h3>
                    <div className="bg-zinc-900 border border-zinc-800 p-4 flex items-center space-x-4 max-w-md">
                      {(() => {
                        const tuner = getMotoTunerById(motoSelection.tuner);
                        return tuner ? (
                          <>
                            {tuner.image_url ? (
                              <img src={tuner.image_url} alt={tuner.name} className="w-16 h-16 object-cover" />
                            ) : (
                              <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center font-bold text-lg">
                                {tuner.name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <p className="font-bold text-lg">{tuner.name}</p>
                            </div>
                          </>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <WelcomeTutorial
        open={showTutorial}
        onClose={() => {
          setShowTutorial(false);
        }}
        onDismissPermanently={() => {
          setShowTutorial(false);
          localStorage.setItem('msf_rules_dismissed', 'true');
        }}
      />
    </div>
    </PageBackground>
  );
}
